import { ENV_CONFIG } from '@/constants/envs/constants'
import { ChainId } from '@/lib/chain'
import { Native, SerializedCryptoType } from '@/lib/cryptoCurrency'
import { SerializedFait } from '@/lib/fiatCurrency/zod'
import { getActiveChainId } from '@/utils'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Address } from 'viem'

export type TThemeOption = 'light' | 'dark' | 'auto'
export enum EApplicationModal {
  AUTH
}

export interface IThemeState {
  value: TThemeOption
  loading: boolean
  error: string | null | undefined
}

interface ICurrentChainResult {
  chainId: ChainId
  token: SerializedCryptoType
}

const getCurrentChain = (): ICurrentChainResult => {
  const storedChainId = getActiveChainId()
  if (storedChainId) {
    const storedChainIdNum = Number(storedChainId) as ChainId

    if (ENV_CONFIG.supportedChains.includes(storedChainIdNum)) {
      return {
        chainId: storedChainIdNum as ChainId,
        token: Native.onChain(storedChainIdNum).serialize()
      }
    }
  }
  return {
    chainId: ENV_CONFIG.defaultChainId,
    token: Native.onChain(ENV_CONFIG.defaultChainId).serialize()
  }
}
export interface IBuyCryptoState {
  readonly chainId: ChainId
  readonly fromFiatCurrency: SerializedFait
  readonly fromFiatCurrencyAmount: string
  readonly toToken: SerializedCryptoType
  readonly toTokenAmount: string
  readonly receiverAddress: Address | ''
  readonly signedQuote: string
  readonly fiatMinLimit: string
  readonly fiatMaxLimit: string
  readonly objectiveOfTrading: string | null
}

const initialChainData = getCurrentChain()

const initialState: IBuyCryptoState = {
  chainId: initialChainData.chainId,
  fromFiatCurrency: {
    name: 'Thai baht',
    symbol: 'THB',
    decimals: 2
  },
  fromFiatCurrencyAmount: ENV_CONFIG.initFromFiatCurrencyAmount ?? '1000',
  toToken: initialChainData.token,
  toTokenAmount: '0',
  receiverAddress: '',
  signedQuote: '',
  fiatMinLimit: '1',
  fiatMaxLimit: '1',
  objectiveOfTrading: null
}

const buyCryptoSlice = createSlice({
  name: 'buyCrpto',
  initialState,
  reducers: {
    setChainId(state, action: PayloadAction<ChainId>) {
      state.chainId = action.payload
    },
    // TODO: should specify type
    setFromFiatCurrency(state, action) {
      state.fromFiatCurrency = action.payload
    },
    setFromFiatCurrencyAmount(state, action: PayloadAction<string>) {
      state.fromFiatCurrencyAmount = action.payload
    },
    setToToken(state, action: PayloadAction<SerializedCryptoType>) {
      state.toToken = action.payload
    },
    setToTokenAmount(state, action) {
      state.toTokenAmount = action.payload
    },
    setSignedQuote(state, action) {
      state.signedQuote = action.payload
    },
    setReceiverAddress(state, action: PayloadAction<Address | ''>) {
      state.receiverAddress = action.payload
    },
    setFiatLimit(
      state,
      action: PayloadAction<{ fiatMinLimit: string; fiatMaxLimit: string }>
    ) {
      state.fiatMinLimit = action.payload.fiatMinLimit
      state.fiatMaxLimit = action.payload.fiatMaxLimit
    },
    setObjectiveOfTrading(state, action: PayloadAction<string>) {
      state.objectiveOfTrading = action.payload
    },
    clearObjectiveOfTrading(state) {
      state.objectiveOfTrading = null
    },
    clearReceiveAddress: (state) => {
      state.receiverAddress = initialState.receiverAddress
    }
  }
})

export const {
  setChainId,
  setFromFiatCurrency,
  setFromFiatCurrencyAmount,
  setToToken,
  setToTokenAmount,
  setSignedQuote,
  setReceiverAddress,
  setFiatLimit,
  setObjectiveOfTrading,
  clearObjectiveOfTrading,
  clearReceiveAddress
} = buyCryptoSlice.actions
export default buyCryptoSlice.reducer
